import React, { use, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AccountTreeOutlined from '@mui/icons-material/AccountTreeOutlined';
import ArrowForward from '@mui/icons-material/ArrowForward';
import CheckRounded from '@mui/icons-material/CheckRounded';
import ModeEditOutlineOutlined from '@mui/icons-material/ModeEditOutlineOutlined';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import InfoIcon from 'assets/icons/info-icon.svg?react';
import PopUp from 'shared/components/pop-up/pop-up';
import Permission from 'shared/features/auth/authorization/enums/permission';
import usePermissionAuthorization from 'shared/features/auth/authorization/hooks/permission-authorization';

import styles from 'features/residents/resident-settings/components/resident-setting-details-header/resident-setting-details-header.module.scss';
import useOrganisationNavigation from 'features/residents/resident-settings/hooks/organisation-navigation';
import ResidentSettingsProviderContext from 'features/residents/resident-settings/providers/contexts/resident-settings-provider-context';

type Props = {
  isScrolling?: boolean;
  isSubmitting: boolean;
  isLoading: boolean;
  hasErrors?: boolean;
};

const ResidentSettingDetailsHeader = ({ isScrolling, isSubmitting, isLoading, hasErrors }: Readonly<Props>): React.JSX.Element => {
  const { t } = useTranslation('residentsSettings');
  const { hasPermission } = usePermissionAuthorization();
  const { navigateToOrganisationUnit } = useOrganisationNavigation();
  const [isMultipleResidentsPopUpOpen, setIsMultipleResidentsPopUpOpen] = useState(false);
  const handleOpenMultipleResidentsPopUp = (): void => setIsMultipleResidentsPopUpOpen(true);
  const handleCloseMultipleResidentsPopUp = (): void => setIsMultipleResidentsPopUpOpen(false);

  const {
    detailEditState: { residentSettingEditForm, viewingMode, currentResidentSetting, enableDetailViewingMode, enableDetailEditMode },
  } = use(ResidentSettingsProviderContext);

  const ecd = currentResidentSetting?.electronicClientRecordClientResult;
  const canNavigateToOrganisation = currentResidentSetting !== undefined && hasPermission(Permission.ReadOrganisationUnit);

  const showEditButton = currentResidentSetting !== undefined;

  const onCancel = (): void => {
    residentSettingEditForm.reset({
      organisationUnitId: currentResidentSetting?.organisationUnitId,
      organisationUnitScenarios: currentResidentSetting?.organisationUnitScenarios,
      devices: currentResidentSetting?.devices,
      note: currentResidentSetting?.note,
      scenariosMuted: currentResidentSetting?.scenariosMuted,
      muteScenariosFrom: currentResidentSetting?.muteScenariosFrom ?? undefined,
      muteScenariosUntil: currentResidentSetting?.muteScenariosUntil ?? undefined,
      muteScenariosReason: currentResidentSetting?.muteScenariosReason ?? undefined,
    });

    enableDetailViewingMode();
  };

  const renderCreationMode = (
    <div className={styles.topHeaderContainer}>
      {hasErrors && (
        <Typography variant="caption" className={styles.error}>
          {t('validationErrorsSummaryText')}
        </Typography>
      )}
      <div className={styles.actionsContainer}>
        <Button data-testid="cancelButton" variant="text" onClick={onCancel}>
          {t('details.cancelFormButton')}
        </Button>
        <Button
          data-testid="submitButton"
          startIcon={<CheckRounded />}
          variant="contained"
          type="submit"
          loading={isSubmitting}
          loadingPosition="start"
        >
          {t('details.saveFormButton')}
        </Button>
      </div>
    </div>
  );

  const renderViewingMode = (
    <div className={styles.actionsContainer}>
      {hasPermission(Permission.UpdateResidentsSettings) && (
        <Button data-testid="editButton" startIcon={<ModeEditOutlineOutlined />} variant="text" onClick={enableDetailEditMode}>
          {t('details.editFormButton')}
        </Button>
      )}
    </div>
  );

  const renderMultipleResidentsPopUpBody = () => {
    const lineBreak = '\n';

    if (!ecd?.clients) {
      return '';
    }

    return (
      t('electronicClientRecordPopup.body', { clientName: ecd?.clients[0]?.clientName }) +
      lineBreak +
      lineBreak +
      ecd?.clients
        .map((client) => {
          const clientName = t('electronicClientRecordPopup.name') + client.clientName + lineBreak;
          const clientResuscitationDecisionType =
            t('electronicClientRecordPopup.resuscitationDecisionType') +
            t(`electronicClientRecordPopup.decisionTypes.${client.resuscitationDecisionType}`) +
            lineBreak;
          const alertsText =
            client.alerts && client.alerts.length > 0
              ? t('electronicClientRecordPopup.alert') + client.alerts.map((alert) => alert.name).join(', ') + lineBreak
              : '';

          return clientName + clientResuscitationDecisionType + alertsText;
        })
        .join(lineBreak)
    );
  };

  const renderMultipleResidentsPopup = (): React.JSX.Element | null => {
    return (
      <PopUp
        isOpen={isMultipleResidentsPopUpOpen}
        title={t('electronicClientRecordPopup.title')}
        body={renderMultipleResidentsPopUpBody() ?? null}
        secondaryButtonText={t('electronicClientRecordPopup.confirm')}
        secondaryButtonAction={handleCloseMultipleResidentsPopUp}
        handleOnClose={handleCloseMultipleResidentsPopUp}
      />
    );
  };

  const renderTitleActions = (): React.JSX.Element => (
    <div className={styles.titleActions}>
      {(ecd?.clients?.length ?? 0) > 1 && (
        <div className={styles.informationIcon} role="button" onClick={handleOpenMultipleResidentsPopUp}>
          <InfoIcon />
        </div>
      )}
      <div className={styles.organisationNavigationButtonContainer}>
        {canNavigateToOrganisation &&
          viewingMode === 'viewing' && (
            <Tooltip title={t('organisationNavigationButtonLabel')}>
              <Button
                aria-label={t('organisationNavigationButtonLabel')}
                className={styles.organisationNavigationButton}
                data-testid="organisationNavigationButton"
                onClick={() => navigateToOrganisationUnit(currentResidentSetting.organisationUnitId)}
              >
                <AccountTreeOutlined />
                <ArrowForward />
              </Button>
            </Tooltip>
          )}
      </div>
    </div>
  );

  return (
    <div className={`${styles.headerContainer} ${isScrolling && 'is-scrolling'}`}>
      {(viewingMode === 'creation' || viewingMode === 'editing') && renderCreationMode}
      {viewingMode === 'viewing' && (
        <>{showEditButton === undefined || !!showEditButton ? renderViewingMode : <div className={styles.actionsContainer} />}</>
      )}
      {!isLoading && (
        <div className={styles.titleContainer}>
          <Typography variant="h3" data-testid="residentDetailHeader" className={styles.residentDetailHeaderText}>
            <div>{ecd?.clients?.[0]?.clientName ?? t('residentOf')}</div>
            {renderTitleActions()}
          </Typography>
          <Typography variant="h3" data-testid="residentDetailHeader" className={styles.residentDetailOrganisationUnitHeaderText}>
            {viewingMode !== 'creation' ? currentResidentSetting?.organisationUnitName : ''}
          </Typography>
        </div>
      )}
      {renderMultipleResidentsPopup()}
    </div>
  );
};

export default ResidentSettingDetailsHeader;
